import { createContext, useEffect, useState } from "react";
import { getUser, logout } from "features/auth";

import Loader from "shared/ui/Loader";
import { NotificationType } from "shared/types/types";
import { User } from "shared/types/types";
import { getFollowedDocuments } from "features/followed";
import { getNotifications } from "features/notifications";
import { useNavigate } from "react-router-dom";
import { delete_files, get_thread_id, loadConversation } from "features/ai-assistant";
import { getUserSubscription } from "../api";

export interface Message {
    id: string;  // pour éviter le rerender des anciens messages
    role: string;
    content: string;
    toolLink?: { [key: string]: string };
    shouldType?: boolean;
    isError?: boolean;  // bulle d'erreur : affichage distinct, jamais persistée
}

type UserContextValue = {
    user: User | null;
    userType: string | null;
    token: string | null;
    notifications: NotificationType[] | null;
    isVerified: boolean | null;
    ai_thread: string | null;
    ai_conversation: Message[];
    user_sub: number;
    setToken: (token: string | null) => void;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    getMe: () => any;
    get_ai_thread: () => Promise<void>;
    logoutFonction: () => void;
    logoutFonctionADV: () => void;
    logout: (token: string | null) => Promise<void>;
    setAi_conversation: React.Dispatch<React.SetStateAction<Message[]>>
};



const UserContext = createContext<UserContextValue>({
    user: null,
    userType: null,
    notifications: null,
    token: null,
    isVerified: null ,
    ai_thread: null,
    ai_conversation: [],
    user_sub: 0,
    setToken: () => {},
    setUser: () => {},
    logoutFonction: () => {},
    get_ai_thread: async () => {},
    logoutFonctionADV: () => {},
    logout: async () => {},
    getMe: async () => {},
    setAi_conversation: () => {}
});

type UserContextProviderProps = {
    children: React.ReactNode;
};

const UserContextProvider: React.FC<UserContextProviderProps> = ({ children }: any) => {
    const [token, setToken] = useState<string | null>(null);
    const [ai_thread, setAi_thread] = useState<string | null>(null);
    const [user_sub, setUser_sub] = useState<number>(0);
    const [ai_conversation, setAi_conversation] = useState<Message[]>(() => loadConversation<Message>());

    const [user, setUser] = useState<User | null>(null);
    const [userType, setUserType] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState<boolean | null>(null);
    const [notifications, setNotifications] = useState<any[] | null>(null); // Replace 'any' with the actual type of notifications
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const type = localStorage.getItem("type");

    useEffect(() => {
        setLoading(false);
        setUserType(type);
        get_ai_thread(); 
    }, [type]);

    useEffect(() => {
        if (!type) return;
        getMe();
        if (type === "individual") {
            getUserNotifications();
            getVerifiedDocument();
            
        }
    }, [type]);

    const getMe = async () => {
        if (!type) return;
        const userResponse = await getUser(type);
        console.log(`getting user subscription`);
        const userSubscription = await getUserSubscription(type);
        setUser_sub(userSubscription.user_offer);
        setUser(userResponse);
        getVerifiedDocument();
        // le thread nécessite une session : re-tenter une fois l'utilisateur authentifié
        get_ai_thread();
        return userResponse;
    };

    const getUserNotifications = async () => {
        try {
            const data = await getNotifications(userType, 10);
            if (Array.isArray(data.notifs)) {
                setNotifications(data.notifs);
            }
        } catch (error) {
            // console.error("Failed to fetch notifications:", error);
        }
        return notifications;
    };

    const getVerifiedDocument = async () => {
        try {
            if (userType === "partner") {
                const response = await getFollowedDocuments(userType);
                const isVerified = response.some((document: any) => document.stateDoc === "SUIVI");
                setIsVerified(isVerified);
            }
            else if (userType === "individual") {
                const response = await getFollowedDocuments(userType);
                const isVerified = response.some((document: any) => document.stateDoc === "SUIVI");

                setIsVerified(isVerified);
            } else {
                setIsVerified(null);
            }
        } catch (error) {
            // console.error("Failed to fetch verified document:", error);
        }
    };

    const get_ai_thread = async () => {
        try {
            const thread_id = await get_thread_id();
            // jamais stocker un objet Error ou un id invalide (l'API retourne null en échec)
            if (typeof thread_id === "string" && thread_id.startsWith("conv")) {
                setAi_thread(thread_id);
            }
        } catch (error) {
            console.error(error)
        }

    }

    const logoutFonction = async () => {
        localStorage.removeItem("assistantConversation");
        console.log(`getting user_id before delete files : ${user?.clientNumber!}`)
        await delete_files(user?.clientNumber!);
        await logout();
        navigate("/");
        setUser(null);
        setAi_thread(null);
        setAi_conversation([]);
        
    };


    const logoutFonctionADV = async () => {
        localStorage.removeItem("assistantConversation")
        await logout();
        navigate("/bpi");
        setUser(null);
    };

    const value: UserContextValue = {
        user,
        setUser,
        userType,
        notifications,
        token,
        isVerified,
        ai_thread,
        ai_conversation,
        user_sub,
        getMe,
        setToken,
        get_ai_thread,
        logoutFonction,
        logoutFonctionADV,
        logout,
        setAi_conversation,
        
    };

    if (loading) {
        return (
            <div>
                <Loader />
            </div>
        );
    }

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export { UserContext, UserContextProvider };
